import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { apiFetch } from '../lib/apiClient';
import { BookOpen, Clock, ArrowLeft, CheckCircle2, Target, AlertCircle, Youtube, PlayCircle, Upload, Image as ImageIcon, Eye, Timer, X } from 'lucide-react';
import confetti from 'canvas-confetti';

export default function ExerciseDetailPage() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tutorials, setTutorials] = useState(null);
  const [loadingTutorials, setLoadingTutorials] = useState(false);
  const [tutorialsError, setTutorialsError] = useState('');
  const [showSubmissions, setShowSubmissions] = useState(false);
  const fileInputRef = useRef(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [submissionsError, setSubmissionsError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completionDuration, setCompletionDuration] = useState('');
  const [roadmapIndex, setRoadmapIndex] = useState(0);

  const fetchSubmissions = async () => {
    setLoadingSubmissions(true);
    setSubmissionsError('');
    try {
      const res = await apiFetch(`/exercise/${exerciseId}/artwork`, { method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || data?.error || 'Failed to load artworks');
      }
      setSubmissions(data.artworks || []);
    } catch (err) {
      setSubmissionsError(err.message || 'An error occurred fetching artworks');
    } finally {
      setLoadingSubmissions(false);
    }
  };
  useEffect(() => {
    let isMounted = true;

    const loadExercise = async () => {
      try {
        const response = await apiFetch(`/exercise/${exerciseId}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const message = data?.message || data?.error || 'Unable to load exercise.';
          throw new Error(message);
        }

        if (isMounted) {
          setExercise(data.exercise || data);
          setShowTimerModal(true);
        }

        const profileRes = await apiFetch(`/auth/profile`, { method: 'GET' });
        const profileData = await profileRes.json().catch(() => ({}));

        if (profileRes.ok && isMounted) {
          const profile = profileData.profile || profileData;
          const availableExercises = profile?.currentRoadmap?.availableExercises || [];
          const idx = availableExercises.findIndex((ex) => ex.exerciseId === exerciseId);
          if (idx >= 0) {
            setRoadmapIndex(idx);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load exercise.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadExercise();

    return () => {
      isMounted = false;
    };
  }, [exerciseId]);

  const fetchTutorials = async () => {
    if (!exercise?.title) return;
    setLoadingTutorials(true);
    setTutorialsError('');
    setTutorials(null);
    try {
      const response = await apiFetch('/tutorials/exercise', {
        method: 'POST',
        body: JSON.stringify({ exerciseTitle: exercise.title }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to fetch tutorials');
      }
      // Assuming data.tutorials contains the array or structure
      setTutorials(data.tutorials || data);
    } catch (err) {
      setTutorialsError(err.message || 'An error occurred fetching tutorials');
    } finally {
      setLoadingTutorials(false);
    }
  };

  const handleUploadStart = () => {
    setUploadError('');
    setUploadSuccess(false);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, or WebP).');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const response = await apiFetch(`/exercise/${exerciseId}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to get upload details');
      }

      if (!data.uploadUrl) {
        throw new Error('No upload URL returned from the server');
      }

      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setUploadSuccess(true);
      if (showSubmissions) {
        fetchSubmissions();
      }
    } catch (err) {
      setUploadError(err.message || 'An error occurred during upload.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const duration = exercise?.duration ? `${exercise.duration} min` : '';
  const difficulty = exercise?.difficultyLevel
    ? Number(exercise.difficultyLevel)
    : null;

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="exercise-detail-page">
      <DashboardSidebar />

      <main className="lg:ml-60 p-4 pt-24 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            className="flex items-center gap-2 text-slate-600 hover:text-[hsl(222,47%,11%)]"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-600">
            <span className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-transparent animate-spin" />
            <span>Loading exercise…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="exercise-error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {!loading && exercise && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
            <div className="col-span-12 lg:col-span-8 space-y-4">
              <Card className="card-elevated border-0">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Badge className="mb-2 bg-[hsl(234,89%,63%)]/10 text-[hsl(234,89%,63%)]">
                        {exercise.styleTrack || 'Exercise'}
                      </Badge>
                      <CardTitle className="text-2xl font-['Outfit'] text-[hsl(222,47%,11%)]">
                        {exercise.title}
                      </CardTitle>
                    </div>
                    <div className="flex flex-col items-end gap-2 text-sm text-slate-600">
                      {duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                          <span>{duration}</span>
                        </div>
                      )}
                      {difficulty != null && (
                        <div className="flex items-center gap-2">
                          <span>Difficulty</span>
                          <div className="flex items-center gap-1">
                            <Progress
                              value={(difficulty / 10) * 100}
                              className="w-24 h-1.5 bg-slate-200"
                            />
                            <span className="text-xs text-slate-500">
                              {difficulty}/10
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-[hsl(222,47%,11%)] mb-1 flex items-center gap-2 text-sm uppercase tracking-wide">
                      <BookOpen className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                      Instructions
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      {exercise.instructions}
                    </p>
                  </div>

                  {Array.isArray(exercise.prerequisites) && exercise.prerequisites.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-[hsl(222,47%,11%)] mb-1 text-sm">
                        Prerequisites
                      </h4>
                      <p className="text-xs text-slate-600">
                        Complete these exercises first: {exercise.prerequisites.join(', ')}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="card-elevated border-0 mt-6">
                <CardHeader>
                  <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2 text-[hsl(222,47%,11%)]">
                    <Youtube className="w-5 h-5 text-red-500" />
                    Recommended Tutorials
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!tutorials && !loadingTutorials && (
                    <div className="flex flex-col items-center justify-center p-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <p className="text-sm text-slate-600 mb-4">
                        Stuck or need inspiration? Get Youtube video tutorials specifically for this exercise.
                      </p>
                      <Button onClick={fetchTutorials} className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white">
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Find Tutorials
                      </Button>
                    </div>
                  )}

                  {loadingTutorials && (
                    <div className="flex flex-col items-center justify-center p-8 gap-3">
                      <span className="w-6 h-6 rounded-full border-2 border-[hsl(234,89%,63%)] border-t-transparent animate-spin" />
                      <span className="text-sm text-slate-600">Finding the best tutorials...</span>
                    </div>
                  )}

                  {tutorialsError && !loadingTutorials && (
                    <div className="flex flex-col items-center justify-center p-6 text-center bg-red-50 text-red-600 rounded-xl border border-red-100">
                      <AlertCircle className="w-8 h-8 mb-2 opacity-50" />
                      <p className="text-sm font-medium">{tutorialsError}</p>
                      <Button onClick={fetchTutorials} variant="outline" className="mt-4 border-red-200 text-red-600 hover:bg-red-50">
                        Try Again
                      </Button>
                    </div>
                  )}

                  {tutorials && Array.isArray(tutorials) && tutorials.length > 0 && !loadingTutorials && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tutorials.map((tutorial, idx) => (
                        <a
                          key={idx}
                          href={tutorial.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block border border-slate-200 rounded-xl overflow-hidden hover:border-[hsl(234,89%,63%)] transition-colors shadow-sm hover:shadow"
                        >
                          {(tutorial.thumbnail || tutorial.thumbnailUrl || tutorial.thumbnail_url) && (
                            <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                              <img src={tutorial.thumbnail || tutorial.thumbnailUrl || tutorial.thumbnail_url} alt={tutorial.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <PlayCircle className="w-10 h-10 text-white opacity-80 group-hover:opacity-100 drop-shadow-md" />
                              </div>
                            </div>
                          )}
                          <div className="p-3">
                            <h4 className="font-semibold text-[hsl(222,47%,11%)] text-sm line-clamp-2 mb-1 group-hover:text-[hsl(234,89%,63%)] transition-colors">
                              {tutorial.title}
                            </h4>
                            <p className="text-xs text-slate-500 truncate">{tutorial.channelTitle || 'YouTube'}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}

                  {tutorials && Array.isArray(tutorials) && tutorials.length === 0 && !loadingTutorials && (
                    <div className="text-center p-6 bg-slate-50 rounded-xl text-slate-500 text-sm">
                      No tutorials found for this exercise.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="card-elevated border-0 mt-6" data-testid="submissions-card">
                <CardHeader>
                  <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2 text-[hsl(222,47%,11%)]">
                    <ImageIcon className="w-5 h-5 text-[hsl(234,89%,63%)]" />
                    Practice Art Submissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!showSubmissions ? (
                    <div className="flex flex-col items-center justify-center p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <div className="w-16 h-16 rounded-2xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center mb-4">
                        <ImageIcon className="w-8 h-8 text-[hsl(234,89%,63%)] opacity-50" />
                      </div>
                      <p className="text-sm font-medium text-[hsl(222,47%,11%)] mb-2">Ready to view your submissions?</p>
                      <p className="text-sm text-slate-600 mb-6 max-w-sm">
                        View your practice artwork for this exercise to track your progress and reflect on your technique.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          className="bg-white border hover:bg-slate-50 text-[hsl(222,47%,11%)]"
                          disabled={loadingSubmissions}
                          onClick={async () => {
                            await fetchSubmissions();
                            setShowSubmissions(true);
                          }}
                        >
                          {loadingSubmissions ? (
                            <span className="w-4 h-4 mr-2 border-2 text-[hsl(222,47%,11%)] border-t-transparent border-[hsl(222,47%,11%)] rounded-full animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4 mr-2" />
                          )}
                          View Practice Art
                        </Button>
                        <Button
                          className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white"
                          onClick={handleUploadStart}
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <span className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {isUploading ? 'Uploading...' : 'Upload your work'}
                        </Button>
                      </div>

                      {uploadError && (
                        <p className="text-sm font-medium text-red-500 mt-4">{uploadError}</p>
                      )}
                      {uploadSuccess && (
                        <p className="text-sm font-medium text-green-600 mt-4">Artwork successfully uploaded!</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-medium text-[hsl(222,47%,11%)]">Your Submissions</h3>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-50 bg-white"
                            onClick={handleUploadStart}
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <span className="w-4 h-4 mr-1 border-2 text-[hsl(222,47%,11%)] border-t-transparent border-[hsl(222,47%,11%)] rounded-full animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Upload More
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowSubmissions(false)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            Hide Submissions
                          </Button>
                        </div>
                      </div>
                      
                      {submissionsError && (
                        <div className="p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {submissionsError}
                        </div>
                      )}

                      {!submissionsError && submissions.length === 0 && !loadingSubmissions ? (
                        <div className="py-8 text-center text-slate-500 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                          No practice artwork uploaded yet.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {submissions.map((sub, idx) => (
                            <div
                              key={sub.key || idx}
                              className="group relative cursor-pointer rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all"
                              onClick={() => window.open(sub.url, '_blank')}
                            >
                              <div className="aspect-square bg-slate-100">
                                <img src={sub.url} alt={sub.filename || 'Submission'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                              </div>
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                                <Eye className="w-8 h-8 text-white mb-2" />
                                <span className="text-xs text-white font-medium">View Full Image</span>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 translate-y-2 group-hover:translate-y-0 transition-transform">
                                <p className="text-white text-xs font-medium truncate">
                                  {sub.lastModified ? new Date(sub.lastModified).toLocaleDateString() : 'Uploaded just now'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                </CardContent>
              </Card>
            </div>

            <div className="col-span-12 lg:col-span-4 space-y-4">
              <Card className="card-elevated border-0">
                <CardHeader>
                  <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2">
                    <Target className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                    Learning Objectives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Array.isArray(exercise.learningObjectives) && exercise.learningObjectives.length > 0 ? (
                    <ul className="space-y-2 text-sm text-slate-700">
                      {exercise.learningObjectives.map((obj, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,36%)] mt-0.5" />
                          <span>{obj}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-500">Objectives will appear here for this exercise.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-elevated border-0">
                <CardHeader>
                  <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2">
                    <Target className="w-4 h-4 text-[hsl(234,89%,63%)]" />
                    Target Skills
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {Array.isArray(exercise.targetSkills) && exercise.targetSkills.length > 0 ? (
                    exercise.targetSkills.map((skill) => (
                      <Badge
                        key={skill}
                        className="bg-[hsl(234,89%,63%)]/10 text-[hsl(234,89%,63%)] text-xs"
                      >
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500">No specific target skills listed.</p>
                  )}
                </CardContent>
              </Card>

              <div className="relative mt-4">
                {showCompletionPopup && (
                  <Card className="absolute bottom-full left-0 right-0 mb-3 shadow-lg border-slate-200 animate-in fade-in slide-in-from-bottom-2 z-10">
                    <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm font-['Outfit']">Completion Details</CardTitle>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowCompletionPopup(false)}>
                        <X className="w-4 h-4 text-slate-500" />
                      </Button>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 space-y-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-700">How many minutes did it take?</label>
                        <Input
                          type="number"
                          placeholder="e.g. 15"
                          value={completionDuration}
                          onChange={(e) => setCompletionDuration(e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <Button
                        className="w-full h-9 bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white text-xs"
                        onClick={() => {
                          if (completionDuration) {
                            const { isCompleted, ...rest } = exercise;
                            const requestBody = {
                              exerciseIndex: roadmapIndex,
                              exerciseData: rest,
                              duration: Number(completionDuration)
                            };

                            apiFetch(`/exercise/${exerciseId}/complete`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(requestBody)
                            })
                              .then(res => res.json())
                              .then(data => {
                                console.log('Successfully completed:', data);
                                sessionStorage.removeItem('sidebar_profile');
                                window.dispatchEvent(new Event('profileUpdate'));

                                // Trigger success confetti animation then redirect to dashboard
                                const confettiDuration = 5000; // ms
                                const end = Date.now() + confettiDuration;

                                const frame = () => {
                                  confetti({
                                    particleCount: 5,
                                    angle: 60,
                                    spread: 55,
                                    origin: { x: 0 },
                                    colors: ['#4ade80', '#22c55e', '#16a34a', '#8b5cf6', '#d946ef']
                                  });
                                  confetti({
                                    particleCount: 5,
                                    angle: 120,
                                    spread: 55,
                                    origin: { x: 1 },
                                    colors: ['#4ade80', '#22c55e', '#16a34a', '#8b5cf6', '#d946ef']
                                  });

                                  if (Date.now() < end) {
                                    requestAnimationFrame(frame);
                                  }
                                };
                                frame();

                                // After the confetti animation completes, redirect to the dashboard
                                setTimeout(() => navigate('/dashboard'), confettiDuration);
                              })
                              .catch(err => console.error('Failed to complete exercise:', err));

                            setShowCompletionPopup(false);
                          }
                        }}
                      >
                        Confirm & Complete
                      </Button>
                    </CardContent>
                  </Card>
                )}

                <Button
                  className="w-full bg-[hsl(142,76%,36%)] hover:bg-[hsl(142,76%,32%)] text-white font-medium h-12 rounded-xl text-base"
                  onClick={() => {
                    setCompletionDuration(exercise?.duration?.toString() || "15");
                    setShowCompletionPopup(true);
                  }}
                >
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Mark Exercise as Completed
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Timer Instructions Modal */}
      <Dialog open={showTimerModal} onOpenChange={setShowTimerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit'] text-[hsl(222,47%,11%)]">
              <Timer className="w-5 h-5 text-[hsl(234,89%,63%)]" />
              Set a Timer!
            </DialogTitle>
            <DialogDescription className="text-slate-600 mt-2">
              Before you start working on this exercise, we strongly recommend setting a timer for the suggested duration ({duration || '15 min'}).
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <p className="text-sm text-slate-700">
              Working within a time limit helps improve line confidence, decision making, and prevents over-working the piece. Focus on capturing the essence rather than perfect details!
            </p>
          </div>

          <DialogFooter className="mt-4 sm:justify-center">
            <Button
              className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white w-full sm:w-auto"
              onClick={() => setShowTimerModal(false)}
            >
              I&apos;m ready to start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

